import { useMemo } from "react";

import type {
  AssetType,
  LifecycleState,
  RiskClassification,
} from "@/api";
import { FilterChips } from "@/components/shared/filter-chip";
import { useTranslation } from "@/i18n";
import type { TranslationKey } from "@/i18n";
import {
  ASSET_TYPES,
  LIFECYCLE_STATES,
  RISK_CLASSIFICATIONS,
} from "@/lib/asset-enums";

// Enum values map to translation keys so filter chip labels follow
// the active locale.
const ASSET_TYPE_KEY: Record<AssetType, TranslationKey> = {
  prompt: "assets.types.prompt",
  model: "assets.types.model",
  agent: "assets.types.agent",
  workflow: "assets.types.workflow",
  provider: "assets.types.provider",
  tool: "assets.types.tool",
  dataset: "assets.types.dataset",
};

const LIFECYCLE_KEY: Record<LifecycleState, TranslationKey> = {
  draft: "assets.status.draft",
  review: "assets.status.review",
  active: "assets.status.active",
  deprecated: "assets.status.deprecated",
  retired: "assets.status.retired",
};

const RISK_KEY: Record<RiskClassification, TranslationKey> = {
  low: "assets.risk.low",
  medium: "assets.risk.medium",
  high: "assets.risk.high",
  critical: "assets.risk.critical",
};

export interface AssetFilterState {
  asset_type: AssetType | null;
  lifecycle_state: LifecycleState | null;
  risk_classification: RiskClassification | null;
}

interface Props {
  value: AssetFilterState;
  onChange: (next: AssetFilterState) => void;
}

export function AssetsFilterBar({ value, onChange }: Props) {
  const { t } = useTranslation();

  const typeOptions = useMemo(
    () => ASSET_TYPES.map((s) => ({ value: s, label: t(ASSET_TYPE_KEY[s]) })),
    [t]
  );
  const lifecycleOptions = useMemo(
    () =>
      LIFECYCLE_STATES.map((s) => ({ value: s, label: t(LIFECYCLE_KEY[s]) })),
    [t]
  );
  const riskOptions = useMemo(
    () =>
      RISK_CLASSIFICATIONS.map((r) => ({ value: r, label: t(RISK_KEY[r]) })),
    [t]
  );

  return (
    <div className="space-y-2">
      <FilterChips<AssetType>
        label={t("assets.filterBar.type")}
        options={typeOptions}
        value={value.asset_type}
        onChange={(next) => onChange({ ...value, asset_type: next })}
      />
      <FilterChips<LifecycleState>
        label={t("assets.filterBar.lifecycle")}
        options={lifecycleOptions}
        value={value.lifecycle_state}
        onChange={(next) => onChange({ ...value, lifecycle_state: next })}
      />
      <FilterChips<RiskClassification>
        label={t("assets.filterBar.risk")}
        options={riskOptions}
        value={value.risk_classification}
        onChange={(next) => onChange({ ...value, risk_classification: next })}
      />
    </div>
  );
}
